import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, GitCompare, TrendingUp, ScanBarcode, Heart, BookOpen, Info, FlaskConical } from "lucide-react";

const ITEMS = [
  { to: "/scan", label: "Scan", icon: ScanBarcode, id: "scan" },
  { to: "/compare", label: "Compare", icon: GitCompare, id: "compare" },
  { to: "/ingredients", label: "Ingredients", icon: BookOpen, id: "ingredients" },
  { to: "/favorites", label: "Saved", icon: Heart, id: "favorites" },
  { to: "/trending", label: "Trending", icon: TrendingUp, id: "trending" },
  { to: "/about", label: "About", icon: Info, id: "about" },
];

export const MobileMenu = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Open menu"
        data-testid="mobile-menu-btn"
        className="md:hidden w-11 h-11 rounded-full ghost-btn flex items-center justify-center"
      >
        <Menu className="h-5 w-5" />
      </button>
      {createPortal(
        <AnimatePresence>
          {open && (
            <>
              <motion.div
                key="backdrop"
                data-testid="mobile-menu-backdrop"
                className="fixed inset-0 z-[900] bg-black/70 backdrop-blur-sm md:hidden"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setOpen(false)}
              />
              <motion.aside
                key="drawer"
                data-testid="mobile-menu-drawer"
                className="fixed top-0 right-0 bottom-0 z-[901] w-[82vw] max-w-[340px] bg-[color:var(--surface)] border-l border-white/10 flex flex-col md:hidden"
                style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", stiffness: 380, damping: 36 }}
              >
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
                  <div className="flex items-center gap-2 font-display font-bold">
                    <div className="h-7 w-7 rounded-md bg-[color:var(--lime)] flex items-center justify-center">
                      <FlaskConical className="h-3.5 w-3.5 text-[#07130a]" strokeWidth={2.5} />
                    </div>
                    WhatIs<span className="text-[color:var(--lime)]">Inside</span>
                  </div>
                  <button
                    onClick={() => setOpen(false)}
                    aria-label="Close menu"
                    data-testid="mobile-menu-close"
                    className="w-11 h-11 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <nav className="flex-1 overflow-y-auto px-3 py-4 font-mono uppercase tracking-wider text-sm">
                  {ITEMS.map(({ to, label, icon: Icon, id }, i) => (
                    <motion.div key={to} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 + i * 0.04 }}>
                      <NavLink
                        to={to}
                        data-testid={`mobile-nav-${id}`}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-4 py-3.5 rounded-lg transition-colors ${
                            isActive ? "text-[color:var(--lime)] bg-[color:var(--lime)]/10" : "text-[color:var(--text-2)] hover:text-[color:var(--text)] hover:bg-white/5"
                          }`
                        }
                      >
                        <Icon className="h-4 w-4" /> {label}
                      </NavLink>
                    </motion.div>
                  ))}
                </nav>
                <div className="px-5 py-4 border-t border-white/5 text-[10px] font-mono tracking-[0.3em] uppercase text-[color:var(--text-3)]">
                  Know what is inside your food
                </div>
              </motion.aside>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default MobileMenu;
